// Libs
import React, { useEffect, useRef } from "react";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";

// Local
import BaseModal from "../BaseModal";
import BaseButtonSecondary from "../BaseButtonSecondary";
import ButtonColored from "../ButtonColored";
import BaseInputTextArea from "../BaseInputTextArea";
import HeadingOne from "../HeadingOne";
import * as strings from "../../strings";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cancellationNote: string) => Promise<void>;
  isSaving: boolean;
  invoiceNumber?: string;
}

export default function CancelInvoiceDialog(props: Props): JSX.Element {
  const [cancellationNote, setCancellationNote] = React.useState("");
  const cancellationNoteRef = useRef<HTMLTextAreaElement>(null);

  // Focus on the textarea when the dialog opens
  useEffect(() => {
    if (props.isOpen && cancellationNoteRef.current) {
      setTimeout(() => {
        cancellationNoteRef.current?.focus();
      }, 100);
    }
  }, [props.isOpen]);

  const handleCancel = () => {
    setCancellationNote("");
    props.onClose();
  };

  const handleConfirm = async () => {
    await props.onConfirm(cancellationNote);
    setCancellationNote("");
  };

  const dialogHeader = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-red-600 p-8 text-left text-white">
      <HeadingOne fontColor="text-white" fontSize="text-xl">
        Cancel Invoice {props.invoiceNumber ? `#${props.invoiceNumber}` : ""}
      </HeadingOne>
      <button type="button" onClick={handleCancel}>
        <XMarkIcon
          aria-label="close cancel invoice dialog"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  return (
    <BaseModal
      open={props.isOpen}
      closeModal={handleCancel}
      title={dialogHeader}
      parentDivStyles="inline-block transform overflow-hidden max-w-lg rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="p-6">
        <p className="mb-4 text-base text-gray-700">
          Add any additional notes for why this invoice is being cancelled.
        </p>
        <div className="mb-4">
          <BaseInputTextArea
            text=""
            admin={true}
            value={cancellationNote}
            onChange={(e) => setCancellationNote(e.target.value)}
            rows={4}
            className="w-full"
            placeholder="Enter notes..."
            ref={cancellationNoteRef}
          />

          <p className="mt-2 text-xs italic text-gray-600">
            Note: This will be saved in the invoice's internal notes.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <ButtonColored
            kind="danger"
            isBusy={props.isSaving}
            busyText="Canceling..."
            onClick={handleConfirm}
          >
            CANCEL INVOICE
          </ButtonColored>
        </div>
      </div>
    </BaseModal>
  );
}
